import React, { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
    ChevronDown, ChevronRight, Calendar, User, Flag, FolderKanban,
    Clock, MessageSquare, Paperclip, X, Play, Square, MoreHorizontal,
    CheckCircle2, RefreshCw, Zap, ListChecks
} from 'lucide-react';
import { format, isToday, isPast, isThisWeek, addDays, isBefore, isAfter, startOfDay, differenceInCalendarDays } from 'date-fns';
import { de } from 'date-fns/locale';
import TaskDetailPanelFull from './TaskDetailPanelFull';

const priorityConfig = {
    1: { label: 'Hoch', color: 'bg-red-500', textColor: 'text-red-500', bgLight: 'bg-red-500/10' },
    2: { label: 'Mittel', color: 'bg-yellow-500', textColor: 'text-yellow-500', bgLight: 'bg-yellow-500/10' },
    3: { label: 'Niedrig', color: 'bg-gray-400', textColor: 'text-gray-400', bgLight: 'bg-gray-400/10' },
    0: { label: 'Nicht dringend', color: 'bg-green-500', textColor: 'text-green-500', bgLight: 'bg-green-500/10' }
};

const TaskListAsana = ({
    tasks,
    users,
    projects,
    customers,
    statusConfig,
    onTaskSelect,
    onOpenFullModal,
    onTaskUpdate,
    onTaskDelete,
    onAddComment,
    onDeleteComment,
    onFileUpload,
    onFileDelete,
    onTimeToggle,
    isTracking,
    activeTaskId,
    selectedTaskId,
    elapsedSeconds,
    formattedTime,
    onStartTracking,
    onStopTracking,
    onCancelTracking,
    activityTypes,
    api,
    showCompleted = false,  // New prop to show completed tasks (for archive view)
    highlightUserIds = []   // Admin-Mitarbeiter-Ansicht: diese Zugewiesenen als Badge neben dem Titel zeigen
}) => {
    const [collapsedSections, setCollapsedSections] = useState({});
    const [editingCell, setEditingCell] = useState(null);
    const [showStopModal, setShowStopModal] = useState(false);
    const [stopModalTask, setStopModalTask] = useState(null);
    const [timeStopForm, setTimeStopForm] = useState({
        customer_id: '',
        activity_type: '',
        description: ''
    });

    const toggleSection = (sectionKey) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Handle stop time from quick button in task list
    const handleQuickStopClick = (task) => {
        if (isTracking && activeTaskId === task.id) {
            // Stopping - show the modal
            setStopModalTask(task);
            setTimeStopForm({
                customer_id: task.customer_id || '',
                activity_type: '',
                description: `Arbeit an: ${task.title}`
            });
            setShowStopModal(true);
        } else {
            // Starting - just call onTimeToggle
            onTimeToggle(task);
        }
    };

    const handleStopTime = async (isPause = false) => {
        try {
            const customer = customers?.find(c => c.id === timeStopForm.customer_id);
            await onStopTracking(
                customer?.id || null,
                customer?.name || (isPause ? 'Pause' : 'Allgemein'),
                timeStopForm.description,
                isPause
            );
            setShowStopModal(false);
            setStopModalTask(null);
            toast.success(isPause ? 'Pause erfasst' : 'Zeit gespeichert');
        } catch (error) {
            console.error('Time save error:', error);
            const errorMsg = error?.response?.data?.detail || error?.message || 'Unbekannter Fehler';
            toast.error(`Fehler: ${errorMsg}`);
        }
    };

    const handleCancelTime = () => {
        if (onCancelTracking && typeof onCancelTracking === 'function') {
            onCancelTracking();
        }
        setShowStopModal(false);
        setStopModalTask(null);
        toast.info('Zeit verworfen');
    };

    const formatTimeDisplay = (seconds) => {
        const hrs = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    // Group tasks by due date
    const groupedTasks = useMemo(() => {
        const now = new Date();
        const today = startOfDay(now);
        const endOfWeek = addDays(today, 7);

        const groups = {
            overdue: { label: 'Überfällig', emoji: '🔴', tasks: [], order: 0 },
            today: { label: 'Heute', emoji: '⚡', tasks: [], order: 1 },
            thisWeek: { label: 'Diese Woche', emoji: '📅', tasks: [], order: 2 },
            later: { label: 'Später', emoji: '📌', tasks: [], order: 3 },
            noDueDate: { label: 'Ohne Fälligkeit', emoji: '📋', tasks: [], order: 4 }
        };

        tasks.forEach(task => {
            // Skip completed/done tasks unless showCompleted is true
            if (!showCompleted && statusConfig[task.status]?.is_done) return;

            if (!task.deadline) {
                groups.noDueDate.tasks.push(task);
            } else {
                const dueDate = startOfDay(new Date(task.deadline));
                if (isBefore(dueDate, today)) {
                    groups.overdue.tasks.push(task);
                } else if (isToday(dueDate)) {
                    groups.today.tasks.push(task);
                } else if (isBefore(dueDate, endOfWeek)) {
                    groups.thisWeek.tasks.push(task);
                } else {
                    groups.later.tasks.push(task);
                }
            }
        });

        // Sort tasks within each group by priority then by deadline
        Object.values(groups).forEach(group => {
            group.tasks.sort((a, b) => {
                if (a.priority !== b.priority) return a.priority - b.priority;
                if (!a.deadline) return 1;
                if (!b.deadline) return -1;
                return new Date(a.deadline) - new Date(b.deadline);
            });
        });

        return Object.entries(groups)
            .filter(([_, group]) => group.tasks.length > 0)
            .sort(([_, a], [__, b]) => a.order - b.order);
    }, [tasks, statusConfig, showCompleted]);

    const handleInlineEdit = useCallback(async (taskId, field, value) => {
        setEditingCell(null);
        await onTaskUpdate(taskId, { [field]: value });
    }, [onTaskUpdate]);

    const handleStatusToggle = useCallback(async (task) => {
        // Find the "done" status
        const doneStatusKey = Object.entries(statusConfig).find(([_, config]) => config.is_done)?.[0];
        if (doneStatusKey && task.status !== doneStatusKey) {
            await onTaskUpdate(task.id, { status: doneStatusKey });
        }
    }, [statusConfig, onTaskUpdate]);

    const formatDeadline = (deadline) => {
        if (!deadline) return '';
        const date = new Date(deadline);
        if (isToday(date)) return 'Heute';
        return format(date, 'd. MMM', { locale: de });
    };

    return (
        <div className="flex h-[calc(100vh-200px)] bg-background rounded-xl border border-border overflow-hidden" data-testid="task-list-asana">
            {/* Left Panel - Task List */}
            <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${selectedTaskId ? 'w-1/2' : 'w-full'}`}>
                {/* Table Header */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-muted/30 text-xs font-medium text-muted-foreground sticky top-0 z-10">
                    <div className="w-8" /> {/* Checkbox spacer */}
                    <div className="flex-1 min-w-[200px]">Aufgabenname</div>
                    <div className="w-24 text-center hidden md:block">Fälligkeit</div>
                    <div className="w-28 text-center hidden lg:block">Projekt</div>
                    <div className="w-28 text-center hidden lg:block">Kunde</div>
                    <div className="w-20 text-center hidden md:block">Priorität</div>
                    <div className="w-32 text-center hidden lg:block">Status</div>
                    <div className="w-28 text-center hidden md:block">Zugewiesen</div>
                    <div className="w-10" /> {/* Actions spacer */}
                </div>

                {/* Task Groups */}
                <ScrollArea className="flex-1">
                    <div className="divide-y divide-border/50">
                        {groupedTasks.map(([key, group]) => {
                            // Section colors based on type
                            const sectionStyles = {
                                overdue: { bg: 'bg-red-500/10', border: 'border-l-4 border-l-red-500', text: 'text-red-600 dark:text-red-400' },
                                today: { bg: 'bg-amber-500/10', border: 'border-l-4 border-l-amber-500', text: 'text-amber-600 dark:text-amber-400' },
                                thisWeek: { bg: 'bg-blue-500/10', border: 'border-l-4 border-l-blue-500', text: 'text-blue-600 dark:text-blue-400' },
                                later: { bg: 'bg-green-500/10', border: 'border-l-4 border-l-green-500', text: 'text-green-600 dark:text-green-400' },
                                noDueDate: { bg: 'bg-muted/30', border: 'border-l-4 border-l-gray-400', text: 'text-muted-foreground' }
                            };
                            const style = sectionStyles[key] || sectionStyles.noDueDate;
                            
                            return (
                            <div key={key} className="group/section">
                                {/* Section Header - Bigger and more colorful */}
                                <button
                                    onClick={() => toggleSection(key)}
                                    className={`w-full flex items-center gap-3 px-4 py-3.5 ${style.bg} ${style.border} hover:brightness-95 transition-all text-left`}
                                    data-testid={`section-${key}`}
                                >
                                    {collapsedSections[key] ? (
                                        <ChevronRight className={`w-5 h-5 ${style.text}`} />
                                    ) : (
                                        <ChevronDown className={`w-5 h-5 ${style.text}`} />
                                    )}
                                    <span className="text-lg">{group.emoji}</span>
                                    <span className={`font-semibold text-base ${style.text}`}>
                                        {group.label}
                                    </span>
                                    <Badge variant="secondary" className={`ml-2 text-sm font-medium ${style.bg}`}>
                                        {group.tasks.length}
                                    </Badge>
                                </button>

                                {/* Tasks in Section */}
                                <AnimatePresence>
                                    {!collapsedSections[key] && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            {group.tasks.map((task) => (
                                                <TaskRow
                                                    key={task.id}
                                                    task={task}
                                                    users={users}
                                                    projects={projects}
                                                    customers={customers}
                                                    statusConfig={statusConfig}
                                                    isSelected={selectedTaskId === task.id}
                                                    isTracking={isTracking && activeTaskId === task.id}
                                                    onSelect={() => onTaskSelect(task)}
                                                    onStatusToggle={() => handleStatusToggle(task)}
                                                    onInlineEdit={handleInlineEdit}
                                                    onTimeToggle={() => handleQuickStopClick(task)}
                                                    editingCell={editingCell}
                                                    setEditingCell={setEditingCell}
                                                    formatDeadline={formatDeadline}
                                                    priorityConfig={priorityConfig}
                                                    highlightUserIds={highlightUserIds}
                                                />
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                            );
                        })}
                    </div>

                    {groupedTasks.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                            <CheckCircle2 className="w-12 h-12 mb-4 opacity-30" />
                            <p className="text-lg font-medium">Keine offenen Aufgaben</p>
                            <p className="text-sm">Alle Aufgaben sind erledigt!</p>
                        </div>
                    )}
                </ScrollArea>
            </div>

            {/* Right Panel - Task Details */}
            <AnimatePresence>
                {selectedTaskId && (
                    <TaskDetailPanelFull
                        task={tasks.find(t => t.id === selectedTaskId)}
                        users={users}
                        projects={projects}
                        customers={customers}
                        statusConfig={statusConfig}
                        onClose={() => onTaskSelect(null)}
                        onOpenFullModal={onOpenFullModal}
                        onUpdate={onTaskUpdate}
                        onDelete={onTaskDelete}
                        onAddComment={onAddComment}
                        onDeleteComment={onDeleteComment}
                        onFileUpload={onFileUpload}
                        onFileDelete={onFileDelete}
                        isTracking={isTracking}
                        activeTaskId={activeTaskId}
                        elapsedSeconds={elapsedSeconds}
                        formattedTime={formattedTime}
                        onStartTracking={onStartTracking}
                        onStopTracking={onStopTracking}
                        onCancelTracking={onCancelTracking}
                        activityTypes={activityTypes}
                        api={api}
                    />
                )}
            </AnimatePresence>

            {/* Stop Timer Modal - iOS Style (same as TimePage) */}
            <Dialog open={showStopModal} onOpenChange={setShowStopModal}>
                <DialogContent className="max-w-md p-0 overflow-hidden">
                    {/* Header with gradient */}
                    <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-6 pb-4">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-3 text-xl">
                                <div className="w-12 h-12 rounded-2xl bg-primary/20 flex items-center justify-center">
                                    <span className="text-2xl">⏱️</span>
                                </div>
                                <div>
                                    <p className="font-semibold">Zeit stoppen</p>
                                    <p className="text-3xl font-mono font-bold text-primary">
                                        {formattedTime || formatTimeDisplay(elapsedSeconds || 0)}
                                    </p>
                                </div>
                            </DialogTitle>
                        </DialogHeader>
                    </div>
                    
                    <div className="p-6 pt-2 space-y-5">
                        {/* Task Info */}
                        {stopModalTask && (
                            <div className="bg-muted/50 rounded-lg p-3">
                                <p className="text-xs text-muted-foreground mb-1">Task</p>
                                <p className="font-medium">{stopModalTask.title}</p>
                            </div>
                        )}

                        {/* Quick Actions - iOS Style Cards */}
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                onClick={() => handleStopTime(true)}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/20 transition-all hover:scale-[1.02]"
                                data-testid="stop-as-pause"
                            >
                                <span className="text-3xl">☕</span>
                                <span className="font-medium text-amber-700 dark:text-amber-400">Pause</span>
                            </button>
                            <button
                                onClick={() => {
                                    setTimeStopForm(prev => ({ ...prev, customer_id: '' }));
                                    handleStopTime(false);
                                }}
                                className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-slate-500/10 hover:bg-slate-500/20 border border-slate-500/20 transition-all hover:scale-[1.02]"
                                data-testid="stop-as-general"
                            >
                                <span className="text-3xl">📋</span>
                                <span className="font-medium text-slate-700 dark:text-slate-400">Allgemein</span>
                            </button>
                        </div>

                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t border-dashed" />
                            </div>
                            <div className="relative flex justify-center">
                                <span className="bg-background px-3 text-xs text-muted-foreground uppercase tracking-wider">oder buchen auf</span>
                            </div>
                        </div>

                        {/* Customer Dropdown with Logos */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>🏢</span> Kunde
                            </Label>
                            <Select 
                                value={timeStopForm.customer_id || 'none'} 
                                onValueChange={(v) => setTimeStopForm(prev => ({ ...prev, customer_id: v === 'none' ? '' : v }))}
                            >
                                <SelectTrigger className="mt-2 h-12 rounded-xl">
                                    <SelectValue placeholder="Kunde auswählen..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Kein Kunde</SelectItem>
                                    {customers?.map(c => (
                                        <SelectItem key={c.id} value={c.id}>
                                            <div className="flex items-center gap-3">
                                                <Avatar className="w-7 h-7 rounded-lg">
                                                    {c.logo_url ? (
                                                        <AvatarImage src={c.logo_url} className="object-cover" />
                                                    ) : null}
                                                    <AvatarFallback 
                                                        style={{ backgroundColor: c.ci_colors?.[0] || '#009dde' }} 
                                                        className="text-xs text-white rounded-lg"
                                                    >
                                                        {c.name?.substring(0, 2).toUpperCase()}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <span className="font-medium">{c.name}</span>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Description */}
                        <div>
                            <Label className="flex items-center gap-2 text-sm font-medium">
                                <span>💬</span> Beschreibung
                            </Label>
                            <Input
                                className="mt-2 h-12 rounded-xl"
                                placeholder="Was wurde gemacht? (optional)"
                                value={timeStopForm.description}
                                onChange={(e) => setTimeStopForm(prev => ({ ...prev, description: e.target.value }))}
                            />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 pt-2">
                            <Button 
                                variant="outline" 
                                className="flex-1 h-12 rounded-xl"
                                onClick={() => setShowStopModal(false)}
                            >
                                Abbrechen
                            </Button>
                            <Button 
                                className="flex-1 h-12 rounded-xl"
                                onClick={() => handleStopTime(false)}
                                disabled={!timeStopForm.customer_id}
                                data-testid="stop-timer-confirm"
                            >
                                Zeit speichern
                            </Button>
                        </div>

                        {/* Cancel Timer Option */}
                        <button
                            onClick={handleCancelTime}
                            className="w-full text-center text-sm text-muted-foreground hover:text-destructive transition-colors"
                        >
                            Timer ohne Speichern abbrechen
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

// Task Row Component
const TaskRow = ({
    task,
    users,
    projects,
    customers,
    statusConfig,
    isSelected,
    isTracking,
    onSelect,
    onStatusToggle,
    onInlineEdit,
    onTimeToggle,
    editingCell,
    setEditingCell,
    formatDeadline,
    priorityConfig,
    highlightUserIds = []
}) => {
    const assignedUser = task.assigned_users?.[0];
    // Mitarbeiter-Badges (Admin-Ansicht): alle Zugewiesenen, die im Filter ausgewaehlt sind
    const highlightedUsers = highlightUserIds.length > 0
        ? (task.assigned_users || []).filter(u => highlightUserIds.includes(u.id))
        : [];
    const project = projects?.find(p => p.id === task.project_id);
    const customer = customers?.find(c => c.id === task.customer_id);
    const isOverdue = task.deadline && isPast(new Date(task.deadline)) && !statusConfig[task.status]?.is_done;
    const overdueDays = isOverdue ? differenceInCalendarDays(new Date(), new Date(task.deadline)) : 0;

    return (
        <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            className={`flex items-center gap-2 px-4 py-2.5 border-b border-border/30 hover:bg-muted/30 transition-all cursor-pointer group
                ${isSelected ? 'bg-primary/5 border-l-2 border-l-primary' : ''}
            `}
            onClick={onSelect}
            data-testid={`task-row-${task.id}`}
        >
            {/* Checkbox */}
            <div className="w-8 flex justify-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={statusConfig[task.status]?.is_done || false}
                    onCheckedChange={onStatusToggle}
                    className="rounded-full w-5 h-5 border-2"
                    data-testid={`task-checkbox-${task.id}`}
                />
            </div>

            {/* Task Name */}
            <div className="flex-1 min-w-[200px] flex items-center gap-2">
                <span className={`font-medium truncate ${statusConfig[task.status]?.is_done ? 'line-through text-muted-foreground' : ''}`}>
                    {task.title}
                </span>
                {highlightedUsers.map(u => (
                    <span
                        key={u.id}
                        className="flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full border text-[11px] font-medium flex-shrink-0"
                        style={{ borderColor: u.profile_color, color: u.profile_color }}
                        title={`${u.first_name} ${u.last_name}`}
                        data-testid={`task-row-member-badge-${task.id}-${u.id}`}
                    >
                        <Avatar className="w-4 h-4">
                            <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white text-[8px]">
                                {u.first_name?.[0]}{u.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        {u.first_name}
                    </span>
                ))}
                {task.is_recurring && <RefreshCw className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
                {isTracking && (
                    <span className="flex items-center gap-1 text-xs text-primary font-medium">
                        <Clock className="w-3 h-3 animate-pulse" />
                    </span>
                )}
                {task.checklist?.length > 0 && (
                    <span
                        className={`text-xs flex items-center gap-0.5 ${task.checklist.every(i => i.done) ? 'text-green-500' : 'text-muted-foreground'}`}
                        title="Checkliste"
                        data-testid={`task-row-checklist-${task.id}`}
                    >
                        <ListChecks className="w-3 h-3" />
                        {task.checklist.filter(i => i.done).length}/{task.checklist.length}
                    </span>
                )}
                {task.comments?.length > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                        <MessageSquare className="w-3 h-3" />
                        {task.comments.length}
                    </span>
                )}
                {task.files?.length > 0 && (
                    <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                        <Paperclip className="w-3 h-3" />
                    </span>
                )}
            </div>

            {/* Due Date */}
            <div 
                className={`w-24 text-center text-sm hidden md:block ${isOverdue ? 'text-red-500 font-medium' : 'text-muted-foreground'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {task.deadline ? (
                    <span className={`inline-flex flex-col items-center px-2 py-1 rounded leading-tight ${isOverdue ? 'bg-red-500/10' : ''}`}>
                        <span>{formatDeadline(task.deadline)}</span>
                        {overdueDays > 0 && (
                            <span className="text-[10px] font-normal opacity-80" data-testid={`task-row-overdue-${task.id}`}>
                                {overdueDays === 1 ? '1 Tag' : `${overdueDays} Tage`} überfällig
                            </span>
                        )}
                    </span>
                ) : (
                    <span className="text-muted-foreground/50">-</span>
                )}
            </div>

            {/* Project */}
            <div className="w-28 text-center hidden lg:block" onClick={(e) => e.stopPropagation()}>
                {project ? (
                    <Badge 
                        variant="outline" 
                        className="text-xs truncate max-w-full"
                        style={{ borderColor: project.primary_color, color: project.primary_color }}
                    >
                        {project.name}
                    </Badge>
                ) : (
                    <span className="text-muted-foreground/50 text-sm">-</span>
                )}
            </div>

            {/* Customer */}
            <div className="w-28 text-center hidden lg:block" onClick={(e) => e.stopPropagation()}>
                {customer ? (
                    <Badge variant="outline" className="text-xs truncate max-w-full bg-primary/5 border-primary/30 text-primary" data-testid={`task-row-customer-${task.id}`}>
                        {customer.name}
                    </Badge>
                ) : (
                    <span className="text-muted-foreground/50 text-sm">-</span>
                )}
            </div>

            {/* Priority */}
            <div className="w-20 text-center hidden md:block" onClick={(e) => e.stopPropagation()}>
                {editingCell === `${task.id}-priority` ? (
                    <Select
                        value={String(task.priority)}
                        onValueChange={(v) => onInlineEdit(task.id, 'priority', Number(v))}
                        onOpenChange={(open) => !open && setEditingCell(null)}
                    >
                        <SelectTrigger className="h-7 text-xs w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">Hoch</SelectItem>
                            <SelectItem value="2">Mittel</SelectItem>
                            <SelectItem value="3">Niedrig</SelectItem>
                            <SelectItem value="0">Nicht dringend</SelectItem>
                        </SelectContent>
                    </Select>
                ) : (
                    <Badge
                        variant="secondary"
                        className={`text-xs cursor-pointer hover:ring-2 hover:ring-primary/20 ${priorityConfig[task.priority]?.bgLight} ${priorityConfig[task.priority]?.textColor}`}
                        onClick={() => setEditingCell(`${task.id}-priority`)}
                    >
                        {priorityConfig[task.priority]?.label}
                    </Badge>
                )}
            </div>

            {/* Status */}
            <div className="w-32 text-center hidden lg:block" onClick={(e) => e.stopPropagation()}>
                {editingCell === `${task.id}-status` ? (
                    <Select
                        value={task.status}
                        onValueChange={(v) => onInlineEdit(task.id, 'status', v)}
                        onOpenChange={(open) => !open && setEditingCell(null)}
                    >
                        <SelectTrigger className="h-7 text-xs w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Object.entries(statusConfig).map(([key, config]) => (
                                <SelectItem key={key} value={key}>
                                    {config.emoji} {config.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <Badge
                        className={`text-xs cursor-pointer hover:ring-2 hover:ring-primary/20 whitespace-nowrap ${statusConfig[task.status]?.color}`}
                        onClick={() => setEditingCell(`${task.id}-status`)}
                    >
                        {statusConfig[task.status]?.emoji} {statusConfig[task.status]?.label}
                    </Badge>
                )}
            </div>

            {/* Assignee */}
            <div className="w-28 flex justify-center hidden md:flex" onClick={(e) => e.stopPropagation()}>
                {assignedUser ? (
                    <div className="flex items-center gap-1.5">
                        <Avatar className="w-6 h-6">
                            <AvatarFallback 
                                style={{ backgroundColor: assignedUser.profile_color }} 
                                className="text-white text-[10px]"
                            >
                                {assignedUser.first_name?.[0]}{assignedUser.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        <span className="text-xs truncate max-w-[60px]">{assignedUser.first_name}</span>
                    </div>
                ) : (
                    <span className="text-muted-foreground/50 text-sm">-</span>
                )}
            </div>

            {/* Actions - Always visible when tracking this task */}
            <div className={`w-10 flex justify-center transition-opacity ${isTracking ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} onClick={(e) => e.stopPropagation()}>
                <Button
                    variant={isTracking ? "default" : "ghost"}
                    size="icon"
                    className={`h-7 w-7 ${isTracking ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
                    onClick={onTimeToggle}
                    data-testid={`task-timer-${task.id}`}
                >
                    {isTracking ? <Square className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                </Button>
            </div>
        </motion.div>
    );
};

export default TaskListAsana;
